export type FileKind =
  | "pdf"
  | "docx"
  | "legacy-doc"
  | "sheet"
  | "pptx"
  | "legacy-ppt"
  | "text"
  | "image"
  | "unknown";

export const ACCEPTED_FILE_TYPES = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".ppt",
  ".pptx",
  ".txt",
  ".log",
  ".md",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".gif"
].join(",");

const EXTENSION_KIND: Record<string, FileKind> = {
  pdf: "pdf",
  docx: "docx",
  doc: "legacy-doc",
  xlsx: "sheet",
  xls: "sheet",
  csv: "sheet",
  pptx: "pptx",
  ppt: "legacy-ppt",
  txt: "text",
  log: "text",
  md: "text",
  markdown: "text",
  png: "image",
  jpg: "image",
  jpeg: "image",
  webp: "image",
  gif: "image"
};

export function getExtension(name: string) {
  const clean = name.split("?")[0].split("#")[0];
  const dot = clean.lastIndexOf(".");
  return dot === -1 ? "" : clean.slice(dot + 1).toLowerCase();
}

export function getFileKind(file: Pick<File, "name" | "type">): FileKind {
  const extension = getExtension(file.name);
  const byExtension = EXTENSION_KIND[extension];
  if (byExtension) {
    return byExtension;
  }

  if (file.type === "application/pdf") return "pdf";
  if (file.type.includes("wordprocessingml")) return "docx";
  if (file.type.includes("spreadsheetml") || file.type === "text/csv") return "sheet";
  if (file.type.includes("presentationml")) return "pptx";
  if (file.type.startsWith("text/")) return "text";
  if (file.type.startsWith("image/")) return "image";
  return "unknown";
}

export function getKindLabel(kind: FileKind) {
  switch (kind) {
    case "pdf":
      return "PDF";
    case "docx":
      return "Word";
    case "legacy-doc":
      return "Word";
    case "sheet":
      return "Sheet";
    case "pptx":
      return "Slides";
    case "legacy-ppt":
      return "Slides";
    case "text":
      return "Text";
    case "image":
      return "Image";
    default:
      return "File";
  }
}

export function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

export function formatDate(timestamp: number) {
  return new Intl.DateTimeFormat("ko-KR", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(timestamp);
}

export function describeLimit(kind: FileKind) {
  if (kind === "legacy-doc") {
    return "이전 Word .doc 파일은 서버 변환 없이 브라우저에서 정확히 렌더링하기 어렵습니다. .docx로 저장하면 바로 열 수 있습니다.";
  }

  if (kind === "legacy-ppt") {
    return "이전 PowerPoint .ppt 파일은 브라우저 단독 렌더링을 지원하지 않습니다. .pptx로 저장하면 슬라이드를 열 수 있습니다.";
  }

  return "지원하지 않는 형식입니다.";
}
